import { useCurrency } from "@/context/CurrencyContext";

/**
 * <Money> — the ONE way to display a monetary value in AI Career OS.
 *
 * Props:
 *   value       number in `from` currency (USD by default)
 *   from        original currency code (default: "USD"). If AI returned an
 *               explicit currency alongside the value, pass it here.
 *   compact     use $12K / £1.2M compact notation
 *   decimals    force N decimals (default: currency-appropriate)
 *   showEstimate  show a "~" prefix + "converted" title when the value was
 *                 converted rather than sourced locally
 *   className
 *
 * Automatically:
 *   - Converts `value` from `from` to the user's preferred currency (unless
 *     the value is already in that currency)
 *   - Formats with Intl.NumberFormat + locale-aware separators
 *   - Marks converted values with `~` and a tooltip explaining they're estimates
 */
export default function Money({
  value,
  from = "USD",
  compact = false,
  decimals,
  showEstimate,
  className = "",
  ...rest
}) {
  const { currency, convert, format } = useCurrency();
  if (value === null || value === undefined || value === "") {
    return <span className={className} {...rest}>—</span>;
  }
  const converted = from === currency ? Number(value) : convert(value, from);
  const isConverted = from !== currency;
  const formatted = format(converted, { compact, decimals });
  if (isConverted && showEstimate) {
    return (
      <span className={className} title={`Converted from ${from} — estimate`} {...rest}>
        ~{formatted}
      </span>
    );
  }
  return <span className={className} {...rest}>{formatted}</span>;
}
